"use client"

import Markdown from "react-markdown"
import remarkGfm from "remark-gfm"
import type { Components } from "react-markdown"

const components: Components = {
  h1: ({ children }) => (
    <h1 className="text-lg font-bold text-foreground mt-5 mb-2 first:mt-0">{children}</h1>
  ),
  h2: ({ children }) => (
    <h2 className="text-base font-semibold text-foreground mt-4 mb-2 first:mt-0">{children}</h2>
  ),
  h3: ({ children }) => (
    <h3 className="text-sm font-semibold text-foreground mt-3 mb-1.5 first:mt-0">{children}</h3>
  ),
  p: ({ children }) => (
    <p className="text-sm text-foreground/85 leading-relaxed mb-3 last:mb-0">{children}</p>
  ),
  ul: ({ children }) => (
    <ul className="text-sm text-foreground/85 leading-relaxed list-disc pl-5 mb-3 space-y-1">{children}</ul>
  ),
  ol: ({ children }) => (
    <ol className="text-sm text-foreground/85 leading-relaxed list-decimal pl-5 mb-3 space-y-1">{children}</ol>
  ),
  li: ({ children }) => <li>{children}</li>,
  blockquote: ({ children }) => (
    <blockquote className="border-l-2 border-primary/40 pl-3 my-3 text-sm text-foreground/70 italic">
      {children}
    </blockquote>
  ),
  code: ({ children, className }) => {
    const isBlock = className?.includes("language-")
    if (isBlock) {
      return (
        <code className={`block bg-secondary/50 rounded-lg px-4 py-3 text-xs font-mono text-foreground/90 overflow-x-auto mb-3 ${className ?? ""}`}>
          {children}
        </code>
      )
    }
    return (
      <code className="bg-secondary/50 rounded px-1.5 py-0.5 text-xs font-mono text-foreground/90">
        {children}
      </code>
    )
  },
  pre: ({ children }) => <pre className="mb-3 last:mb-0">{children}</pre>,
  a: ({ href, children }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-primary hover:text-primary/80 underline underline-offset-2 transition-colors"
    >
      {children}
    </a>
  ),
  strong: ({ children }) => <strong className="font-semibold text-foreground">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  hr: () => <hr className="border-border/40 my-4" />,
  table: ({ children }) => (
    <div className="overflow-x-auto mb-3">
      <table className="text-xs w-full border-collapse">{children}</table>
    </div>
  ),
  thead: ({ children }) => (
    <thead className="border-b border-border/40 text-left">{children}</thead>
  ),
  th: ({ children }) => (
    <th className="px-3 py-1.5 font-semibold text-foreground/80">{children}</th>
  ),
  td: ({ children }) => (
    <td className="px-3 py-1.5 text-foreground/70 border-b border-border/20">{children}</td>
  ),
  del: ({ children }) => (
    <del className="text-foreground/50 line-through">{children}</del>
  ),
  input: ({ checked, ...props }) => (
    <input
      type="checkbox"
      checked={checked}
      readOnly
      className="mr-1.5 accent-primary"
      {...props}
    />
  ),
}

/**
 * PostMarkdown — renders markdown content in community post descriptions.
 * Supports GFM (tables, strikethrough, task lists, autolinks).
 */
export function PostMarkdown({ content }: { content: string }) {
  return (
    <Markdown remarkPlugins={[remarkGfm]} components={components}>
      {content}
    </Markdown>
  )
}
